import type { FlightOptionsContainer } from '#containers/FlightOptionsContainer.js';
import type { RoomOptionsContainer } from '#containers/RoomOptionsContainer.js';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { isRateAvailableForMetadata } from './occupancy-utils.js';

/** The feature containers `isItemUnavailable` needs to look up live flight/room availability. */
export interface AvailabilityContainers {
  flightOptions: FlightOptionsContainer;
  roomOptions: RoomOptionsContainer;
}

/**
 * Checks whether a flight or room item is unavailable by looking up the cached Firebolt model.
 * Returns `true` when the item is confirmed unavailable; `false` otherwise (including when the model
 * hasn't loaded yet or the item type doesn't support availability checks).
 */
export function isItemUnavailable(
  itemType: string,
  referenceId: string | undefined,
  metadata: Record<string, unknown> | undefined,
  tripId: string | undefined,
  containers: AvailabilityContainers,
): boolean {
  if (!tripId || !referenceId) return false;

  if (itemType === 'flight') {
    const handle = containers.flightOptions.ClientFlightOption.get({ tripId, id: referenceId });
    if (handle.isReady && handle.data) {
      return !handle.data.isAvailable;
    }
  }

  if (itemType === 'room') {
    const handle = containers.roomOptions.ClientRoomOption.get({ tripId, id: referenceId });
    if (handle.isReady && handle.data) {
      // Identity is `(productCode, occupancy)`; falls back to legacy selectedRateKey via the helper.
      return !isRateAvailableForMetadata(
        handle.data,
        metadata as { productCode?: string; occupancy?: Occupancy; selectedRateKey?: string } | undefined,
      );
    }
  }

  return false;
}
